/**
 * FieldSelector Component
 *
 * Provides a dropdown for selecting which field to filter on.
 * Shows field type icons and handles field changes.
 *
 * @module FilterEditor/FieldSelector
 */

import React from 'react';
import type { LinkedTableFilterApi, TableFieldApiResponse } from '@loader/shared-types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@loader/ui';

import { getTypeIcon } from '@/features/schemas/constants/fieldTypes';

interface FieldSelectorProps {
  /** Whether the user can edit (false for viewers) */
  canEdit?: boolean;
  filter: LinkedTableFilterApi;
  availableFields: TableFieldApiResponse[];
  selectedField: TableFieldApiResponse | undefined;
  onFieldChange: (fieldId: string) => void;
}

export const FieldSelector: React.FC<FieldSelectorProps> = ({
  canEdit = true,
  filter,
  availableFields,
  selectedField,
  onFieldChange,
}) => {
  return (
    <Select value={filter.field_id} onValueChange={onFieldChange} disabled={!canEdit}>
      <SelectTrigger className="bg-background w-full">
        {filter.field_id && selectedField ? (
          <div className="flex items-center gap-2">
            {React.createElement(getTypeIcon(selectedField.data_type), {
              className: 'h-4 w-4 text-muted-foreground shrink-0',
            })}
            <span className="truncate">{selectedField.name}</span>
          </div>
        ) : (
          <SelectValue placeholder="Select field" />
        )}
      </SelectTrigger>
      <SelectContent className="bg-background">
        {availableFields.length === 0 ? (
          <SelectItem value="no-fields" disabled>
            No fields available
          </SelectItem>
        ) : (
          availableFields.map((field) => {
            const TypeIcon = getTypeIcon(field.data_type);
            return (
              <SelectItem key={field.id} value={field.id}>
                <div className="flex items-center gap-2">
                  <TypeIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span>{field.name}</span>
                </div>
              </SelectItem>
            );
          })
        )}
      </SelectContent>
    </Select>
  );
};
